import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { Stack, Button } from '@mui/material';
import * as carApi from '../../lib/api/car';
import CarRegForm from '../../components/carmng/CarRegForm';
import * as Yup from 'yup';

const CarRegContainer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const { type, data, activeReservation } = location.state || {};
  const isNew = type === 'new';
  const hasRes = activeReservation && typeof activeReservation === 'object';
  const editData = Array.isArray(data) ? data[0] : data;
  const savedUser = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);

  const [pickerTarget, setPickerTarget] = useState(null);
  const [partnerDisplayName, setPartnerDisplayName] = useState('');
  const [isListOpen, setIsListOpen] = useState(false);
  const [isPartnerSelected, setIsPartnerSelected] = useState(false);
  const [visitList, setVisitList] = useState([]);

  const initialValues = useMemo(() => ({
    logId: isNew ? '' : (editData?.logId || ''), 
    reservId: isNew ? activeReservation.reservId : (editData?.reservId || ''),
    usrUuid: savedUser.usrUuid || '',
    dptUuid: savedUser.dptUuid || '',
    carNumber: hasRes ? activeReservation.carNumber : (editData?.carNumber || ''),
    carUuid: hasRes ? activeReservation.carUuid : (editData?.carUuid || ''),
    carModel: hasRes ? activeReservation.carModel : (editData?.carModel || ''),
    startDate: isNew ? new Date(): new Date(editData.startDate),
    endDate: !isNew && editData?.endDate ? new Date(editData.endDate) : new Date(),
    visitPartner: isNew? '' : (editData?.visitPartner || ''),
    visitPartnerNm: isNew ? '' : (editData?.visitPartnerNm || ''),
    beforeMileage: isNew ? '' : (editData?.beforeMileage || ''),
    afterMileage: editData?.afterMileage || '',
    commuteMileage: editData?.commuteMileage || '',
    businessMileage: editData?.businessMileage || '',
    carIssue: editData?.carIssue || 'N',
    carIssueDetail: editData?.carIssueDetail || '',
    washCar: editData?.washCar || 'N',
    oilFee: editData?.oilFee || '',
    confirm: editData?.confirm || '',
    comment: editData?.comment || '',
    parking: activeReservation?.parking || '',
    usrNm: savedUser.usrNm || '',
    dptNm: savedUser.dptNm || '',
  }), [editData, savedUser, activeReservation, hasRes, isNew]);

  const validationSchema = Yup.lazy(() => {
    // 신규 등록(isNew === true)일 때는 검증하지 않음 (빈 객체 스키마 반환)
    if (isNew) return Yup.object().shape({});

    // !isNew일 때만 아래 검증 로직 작동
    return Yup.object().shape({
      afterMileage: Yup.number()
        .typeError('숫자만 입력 가능합니다.')
        .positive('0보다 커야 합니다.')
        .required('주행 후 거리를 입력해주세요.'),

      fuelCost: Yup.number()
        .typeError('숫자만 입력 가능합니다.')
        .min(0, '0원 이상 입력 가능합니다.'),

      comment: Yup.string().max(500, '최대 500자까지 입력 가능합니다.'),

      parking: Yup.string().when('returnYn', {
        is: 'Y',
        then: (schema) => schema.required('주차위치를 선택하세요.'),
        otherwise: (schema) => schema.notRequired(),
      }),
    });
  });

const executeSave = async (values) => {
  try {
        if (isNew && !isPartnerSelected) {
        alert("돋보기를 눌러 거래처를 선택해 주세요.");
        return;
      }

    // 1. 데이터 가공 (handleSubmitData의 로직 반영)
    const payload = {
      logId: values.logId || null, // 신규일 때 빈 문자열('') 대신 null 권장
      reservId : values.reservId,
      usrUuid: values.usrUuid,
      dptUuid: values.dptUuid,
      carNumber: values.carNumber, // values.carUuid 대신 실제 차량번호(carNumber) 전달 확인 필요
      carUuid: values.carUuid,     // 서버에서 UUID를 따로 받는다면 추가
      visitPartner: values.visitPartner,
      
      // 날짜 포맷팅
      startDate: format(new Date(values.startDate), 'yyyy-MM-dd HH:mm:ss'),
      endDate: !isNew ? format(new Date(values.endDate), 'yyyy-MM-dd HH:mm:ss') : null,
      
      // 소수점 1자리 고정 및 숫자 변환 (안정성 강화)
      beforeMileage: Number(Number(values.beforeMileage || 0).toFixed(1)),
      afterMileage: values.afterMileage ? Number(Number(values.afterMileage).toFixed(1)) : null,
      commuteMileage: Number(Number(values.commuteMileage || 0).toFixed(1)),
      businessMileage: Number(Number(values.businessMileage || 0).toFixed(1)),
      
      oilFee: Math.round(Number(values.oilFee || 0)),
      carIssue: values.carIssue,
      carIssueDetail: values.carIssueDetail,
      washCar: values.washCar,
      confirm: values.confirm || (isNew ? null : 'S'),
      comment: values.comment || '',
      parking: values.parking || '',
      
    };
    // 2. API 호출
    await carApi.registerUsageLog(payload);
    
    enqueueSnackbar(isNew ? '금일 운행을 시작합니다.' : '금일 운행 기록을 등록합니다.', { variant: 'success' });
    navigate('/carusage');
  } catch (err) {
    const message = err.response?.data?.message || err.message;
    enqueueSnackbar(message, { variant: 'error' });
  }
};


  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values) => {      
      if (!isNew) {
        const isReturn = values.returnYn === 'Y';
        const alertMsg = isReturn 
          ? "차량을 반납시 남은 예약 기간도 종료 됩니다. 반납하시겠습니까? 반납 후 수정이 불가합니다." 
          : "금일 운행 기록을 등록 하시겠습니까? 등록 후 수정이 불가합니다.";

        enqueueSnackbar(alertMsg, {
          variant: isReturn ? 'error' : 'warning', 
          persist: true,
          action: (key) => (
            <Stack direction="row" spacing={1}>
              <Button size="small" color="inherit" onClick={() => closeSnackbar(key)}>취소</Button>
              <Button 
                size="small" 
                variant="contained" 
                color={isReturn ? "primary" : "error"} // 반납일 때는 파란색, 종료일 때는 빨간색(선택사항)
                onClick={() => { 
                  executeSave({ ...values, confirm: 'S' }); 
                  closeSnackbar(key); 
                }}
              >
                확인
              </Button>
            </Stack>
          ),
        });
      } else {
        executeSave(values);
      }

    },
  });

  useEffect(() => {
    if (editData) {
      setPartnerDisplayName(editData.visitPartnerNm || '');
    }
  }, [activeReservation, isNew, editData]);

  return (
    <CarRegForm 
      formik={formik} isNew={isNew} partnerDisplayName={partnerDisplayName}
      pickerTarget={pickerTarget} setPickerTarget={setPickerTarget}
      isListOpen={isListOpen}
      setIsListOpen={setIsListOpen}
      isPartnerSelected={isPartnerSelected}
      setIsPartnerSelected={setIsPartnerSelected}
      setPartnerDisplayName={setPartnerDisplayName}
      visitList={visitList}
      setVisitList={setVisitList}
    />
  );
};

export default CarRegContainer;
